import React, { useCallback, useEffect, useState, useMemo, useRef } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import {
  Ruler, X, Crosshair, Check, Trash2, Loader2, Hexagon, Undo2,
  Spline, MapPin, RotateCcw, Circle, CircleDot, Triangle, Waypoints, Hash,
  ChevronDown, Flag, ScanLine,
} from "lucide-react";
import {
  distanceFt, formatFeet, isCalibrated, polygonAreaSf, formatArea,
  angleDegrees, arcLengthFt, polylineLengthFt,
} from "@/lib/sheetScale";
import { resolveScaleForPoints } from "@/lib/scaleResolution";
import { LENGTH_UNIT_CHOICES, formatLength, formatAreaValue, measurementDisplayLabel } from "@/lib/units";
import { useUnitPreference } from "@/contexts/UnitPreferenceContext";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
} from "@/components/ui/dropdown-menu";

/**
 * MeasureLayer — Iter 33 Field Measurement System.
 *
 * Four tools live behind the same single Measure entry:
 *   distance    — tap A, tap B → instant result + auto-save
 *   polyline    — tap N points, double-tap last point to finish → length
 *   area        — tap corners, close shape → SF / SY / acres
 *   coordinate  — single tap → lat/lon (requires GPS calibration)
 *
 * All four auto-persist via /api/sheets/{id}/measurements the moment the
 * gesture completes. There is NO confirm step.
 */

const ORANGE = "var(--fop-primary)";


/* Polygon centroid in image-pixel coords. Used to anchor the area label. */
function polygonCentroid(points, W, H) {
  if (!points || points.length === 0) return null;
  if (points.length < 3) {
    const p = points[points.length - 1];
    return { x: p.nx * W, y: p.ny * H };
  }
  let A = 0, cx = 0, cy = 0;
  for (let i = 0; i < points.length; i++) {
    const p = points[i];
    const q = points[(i + 1) % points.length];
    const cross = p.nx * q.ny - q.nx * p.ny;
    A += cross;
    cx += (p.nx + q.nx) * cross;
    cy += (p.ny + q.ny) * cross;
  }
  if (Math.abs(A) < 1e-9) {
    const p = points[0];
    return { x: p.nx * W, y: p.ny * H };
  }
  return { x: (cx / (3 * A)) * W, y: (cy / (3 * A)) * H };
}

export function useSheetScale(sheetId) {
  const [scale, setScale] = useState(null);
  const [loaded, setLoaded] = useState(false);
  useEffect(() => {
    // GH-22 (real-plan certification, 2026-10-01) — reset IMMEDIATELY on
    // every sheet change, synchronously, before the new fetch even
    // starts. Previously the PREVIOUS sheet's scale stayed live in
    // state during the fetch window, so anything reading `sheetScale`
    // right after switching sheets (e.g. a live measurement preview)
    // could briefly compute against the wrong sheet's calibration.
    setScale(null);
    setLoaded(false);
    if (!sheetId) return;
    let cancel = false;
    (async () => {
      try {
        const { data } = await api.get(`/sheets/${sheetId}/scale`);
        if (!cancel) { setScale(data); setLoaded(true); }
      } catch {
        if (!cancel) { setScale(null); setLoaded(true); }
      }
    })();
    return () => { cancel = true; };
  }, [sheetId]);

  const save = async (payload) => {
    const { data } = await api.put(`/sheets/${sheetId}/scale`, payload);
    setScale(data);
    return data;
  };
  const clear = async () => {
    await api.delete(`/sheets/${sheetId}/scale`);
    setScale(null);
  };
  return { scale, loaded, save, clear };
}

/* Connected-workflow program (2026-09-10) — sibling hook for the
 * region-scoped ("detail region") scale calibrations. Kept separate
 * from useSheetScale's single-document GET/PUT/DELETE since regions
 * are a list with their own create/edit/remove lifecycle. */
export function useSheetScaleRegions(sheetId) {
  const [regions, setRegions] = useState([]);
  const [loaded, setLoaded] = useState(false);
  // GH-22 (real-plan certification, 2026-10-01) — `reload` had no
  // cancellation guard at all. Switching sheets quickly (common on a
  // tablet flipping through a plan set) could let an IN-FLIGHT reload
  // for the PREVIOUS sheet resolve after a newer reload for the
  // CURRENT sheet already finished, overwriting `regions` with the
  // wrong sheet's calibration boxes. Also reset `regions`/`loaded`
  // synchronously on every sheet change for the same reason
  // `useSheetScale` above does — the live measurement preview must
  // never briefly see a stale sheet's regions.
  const requestIdRef = useRef(0);

  const reload = useCallback(async () => {
    const myRequestId = ++requestIdRef.current;
    if (!sheetId) { setRegions([]); setLoaded(false); return; }
    try {
      const { data } = await api.get(`/sheets/${sheetId}/scales`);
      if (requestIdRef.current !== myRequestId) return; // superseded
      setRegions(Array.isArray(data) ? data : []);
    } catch {
      if (requestIdRef.current !== myRequestId) return;
      setRegions([]);
    } finally {
      if (requestIdRef.current === myRequestId) setLoaded(true);
    }
  }, [sheetId]);

  useEffect(() => {
    setRegions([]);
    setLoaded(false);
    reload();
  }, [reload]);

  const create = async (payload) => {
    const { data } = await api.post(`/sheets/${sheetId}/scales`, payload);
    await reload();
    return data;
  };
  const update = async (scaleId, payload) => {
    const { data } = await api.patch(`/sheets/${sheetId}/scales/${scaleId}`, payload);
    await reload();
    return data;
  };
  const remove = async (scaleId) => {
    await api.delete(`/sheets/${sheetId}/scales/${scaleId}`);
    await reload();
  };
  return { regions, loaded, reload, create, update, remove };
}

/* ============================================================
 * Detail-region overlay — draws named calibration-region boxes
 * (and, mid-flow, the box the operator is currently drawing) so the
 * applicable scale for any area of the sheet is visible at a glance,
 * not just discoverable after the fact in a measurement's history.
 * Connected-workflow program (2026-09-10).
 * ============================================================ */
export function RegionBoxesLayer({ regions, draftBbox, imgDims, viewScale }) {
  const W = imgDims?.w || 1;
  const H = imgDims?.h || 1;
  const hasRegions = Array.isArray(regions) && regions.length > 0;
  if (!hasRegions && !draftBbox) return null;
  const sw = 1.5 / (viewScale || 1);
  return (
    <svg className="absolute inset-0 pointer-events-none" viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" data-testid="region-boxes-layer">
      {hasRegions && regions.map((r) => {
        const [x0, y0, x1, y1] = r.region_bbox || [];
        if ([x0, y0, x1, y1].some((v) => typeof v !== "number")) return null;
        return (
          <g key={r.id} data-testid={`region-box-${r.id}`}>
            <rect
              x={x0 * W} y={y0 * H} width={(x1 - x0) * W} height={(y1 - y0) * H}
              fill="none" stroke="var(--fop-primary)" strokeWidth={sw} strokeDasharray={`${sw * 4} ${sw * 3}`}
              vectorEffect="non-scaling-stroke"
            />
            <text x={x0 * W + 4 / viewScale} y={y0 * H + 12 / viewScale} fontFamily="ui-monospace,Menlo,monospace"
                  fontSize={10 / viewScale} fontWeight="800" fill="var(--fop-primary)"
                  style={{ paintOrder: "stroke", stroke: "var(--fop-bg)", strokeWidth: 3 / viewScale }}>
              {r.region_label || r.label || "Region"}
            </text>
          </g>
        );
      })}
      {draftBbox && (() => {
        const [x0, y0, x1, y1] = draftBbox;
        return (
          <rect
            x={x0 * W} y={y0 * H} width={(x1 - x0) * W} height={(y1 - y0) * H}
            fill="var(--fop-cta-bg)" fillOpacity="0.12" stroke="var(--fop-cta-bg)" strokeWidth={sw}
            strokeDasharray={`${sw * 2} ${sw * 2}`} vectorEffect="non-scaling-stroke"
          />
        );
      })()}
    </svg>
  );
}

/* ============================================================
 * SVG measurement overlay — purely visual. Receives state from
 * the parent (which owns the click handling so it interoperates
 * with SheetCanvas's pointer-event lifecycle).
 * ============================================================ */
export function MeasureSvgLayer({ points, scale, hoverPoint, calibrationMode, areaMode, polylineMode, coordinateMode, radiusMode, diameterMode, angleMode, arcMode, countMode, lengthUnit, imgDims, viewScale, onVertexDrag }) {
  if (!points || points.length === 0) return null;
  const W = imgDims?.w || 1;
  const H = imgDims?.h || 1;
  const unit = lengthUnit || "ft";

  // -------------------- COUNT MODE --------------------
  if (countMode) {
    return (
      <svg className="absolute inset-0 pointer-events-none" viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" data-testid="measure-svg">
        {points.map((p, i) => (
          <g key={i}>
            <circle cx={p.nx * W} cy={p.ny * H} r={16 / viewScale} fill={ORANGE} fillOpacity="0.22" />
            <circle cx={p.nx * W} cy={p.ny * H} r={10 / viewScale} fill={ORANGE} stroke="#fff" strokeWidth={2 / viewScale} vectorEffect="non-scaling-stroke" />
            <text x={p.nx * W} y={p.ny * H} textAnchor="middle" dominantBaseline="middle" fontFamily="ui-monospace,Menlo,monospace" fontSize={10 / viewScale} fontWeight="800" fill="#fff">{i + 1}</text>
          </g>
        ))}
      </svg>
    );
  }

  // -------------------- ANGLE MODE (A → vertex → B) --------------------
  if (angleMode) {
    const all = hoverPoint ? [...points, hoverPoint] : points;
    if (all.length === 0) return null;
    const pathD = all.map((p, i) => `${i === 0 ? "M" : "L"} ${p.nx * W} ${p.ny * H}`).join(" ");
    const deg = points.length === 3 ? angleDegrees(points, imgDims) : (all.length === 3 ? angleDegrees(all, imgDims) : null);
    const vertex = points[1] || all[1];
    const labelXY = vertex ? { x: vertex.nx * W, y: vertex.ny * H - 26 / viewScale } : null;
    return (
      <svg className="absolute inset-0 pointer-events-none" viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" data-testid="measure-svg">
        <path d={pathD} stroke={ORANGE} strokeOpacity="0.35" strokeWidth={14 / viewScale} fill="none" strokeLinejoin="round" strokeLinecap="round" vectorEffect="non-scaling-stroke" />
        <path d={pathD} stroke={ORANGE} strokeWidth={4 / viewScale} fill="none" strokeLinejoin="round" strokeLinecap="round" vectorEffect="non-scaling-stroke" />
        {all.map((p, i) => (
          <g key={i}>
            <circle cx={p.nx * W} cy={p.ny * H} r={22 / viewScale} fill={ORANGE} fillOpacity="0.18" />
            <circle cx={p.nx * W} cy={p.ny * H} r={12 / viewScale} fill={ORANGE} stroke="#fff" strokeWidth={2.5 / viewScale} vectorEffect="non-scaling-stroke" />
          </g>
        ))}
        {deg != null && labelXY && (
          <g style={{ transform: `translate(${labelXY.x}px, ${labelXY.y}px)` }}>
            <text x={0} y={0} textAnchor="middle" dominantBaseline="middle" fontFamily="ui-monospace,Menlo,monospace" fontSize={18 / viewScale} fontWeight="800" fill={ORANGE} stroke="#ffffff" strokeWidth={5 / viewScale} strokeLinejoin="round" paintOrder="stroke" style={{ paintOrder: "stroke" }}>
              {`${deg.toFixed(1)}\u00b0`}
            </text>
          </g>
        )}
      </svg>
    );
  }

  // -------------------- ARC MODE (≥3 points, best-fit circle length) --------------------
  if (arcMode) {
    const all = hoverPoint ? [...points, hoverPoint] : points;
    if (all.length === 0) return null;
    const pathD = all.map((p, i) => `${i === 0 ? "M" : "L"} ${p.nx * W} ${p.ny * H}`).join(" ");
    const ft = points.length >= 3 ? arcLengthFt(scale, points) : null;
    const last = points[points.length - 1];
    const labelXY = last ? { x: last.nx * W, y: last.ny * H - 24 / viewScale } : null;
    return (
      <svg className="absolute inset-0 pointer-events-none" viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" data-testid="measure-svg">
        <path d={pathD} stroke={ORANGE} strokeOpacity="0.35" strokeWidth={14 / viewScale} fill="none" strokeLinejoin="round" strokeLinecap="round" vectorEffect="non-scaling-stroke" />
        <path d={pathD} stroke={ORANGE} strokeWidth={4 / viewScale} fill="none" strokeLinejoin="round" strokeLinecap="round" vectorEffect="non-scaling-stroke" strokeDasharray={`${8 / viewScale} ${4 / viewScale}`} />
        {points.map((p, i) => (
          <g key={i}>
            <circle cx={p.nx * W} cy={p.ny * H} r={20 / viewScale} fill={ORANGE} fillOpacity="0.18" />
            <circle cx={p.nx * W} cy={p.ny * H} r={11 / viewScale} fill={ORANGE} stroke="#fff" strokeWidth={2.5 / viewScale} vectorEffect="non-scaling-stroke" />
          </g>
        ))}
        {ft != null && labelXY && (
          <g style={{ transform: `translate(${labelXY.x}px, ${labelXY.y}px)` }}>
            <text x={0} y={0} textAnchor="middle" dominantBaseline="middle" fontFamily="ui-monospace,Menlo,monospace" fontSize={18 / viewScale} fontWeight="800" fill={ORANGE} stroke="#ffffff" strokeWidth={5 / viewScale} strokeLinejoin="round" paintOrder="stroke" style={{ paintOrder: "stroke" }}>
              {`⌢ ${formatLength(ft, unit)}`}
            </text>
          </g>
        )}
      </svg>
    );
  }

  // -------------------- RADIUS / DIAMETER MODE (2-point) --------------------
  if (radiusMode || diameterMode) {
    const all = hoverPoint ? [...points, hoverPoint] : points;
    const a = all[0];
    const b = all[1];
    if (!a) return null;
    if (!b) {
      return (
        <svg className="absolute inset-0 pointer-events-none" viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" data-testid="measure-svg">
          <circle cx={a.nx * W} cy={a.ny * H} r={22 / viewScale} fill={ORANGE} fillOpacity="0.18" />
          <circle cx={a.nx * W} cy={a.ny * H} r={12 / viewScale} fill={ORANGE} stroke="#fff" strokeWidth={2.5 / viewScale} vectorEffect="non-scaling-stroke" />
        </svg>
      );
    }
    const ft = distanceFt(scale, a, b);
    const midX = (a.nx + b.nx) / 2 * W;
    const midY = (a.ny + b.ny) / 2 * H;
    const prefix = radiusMode ? "R=" : "\u2300=";
    const circleR = radiusMode ? Math.hypot((b.nx - a.nx) * W, (b.ny - a.ny) * H) : Math.hypot((b.nx - a.nx) * W, (b.ny - a.ny) * H) / 2;
    const circleCx = radiusMode ? a.nx * W : midX;
    const circleCy = radiusMode ? a.ny * H : midY;
    return (
      <svg className="absolute inset-0 pointer-events-none" viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" data-testid="measure-svg">
        <circle cx={circleCx} cy={circleCy} r={circleR} fill="none" stroke={ORANGE} strokeOpacity="0.5" strokeWidth={2 / viewScale} strokeDasharray={`${6 / viewScale} ${4 / viewScale}`} vectorEffect="non-scaling-stroke" />
        <line x1={a.nx * W} y1={a.ny * H} x2={b.nx * W} y2={b.ny * H} stroke={ORANGE} strokeWidth={4 / viewScale} strokeLinecap="round" vectorEffect="non-scaling-stroke" />
        <circle cx={a.nx * W} cy={a.ny * H} r={12 / viewScale} fill={ORANGE} stroke="#fff" strokeWidth={2.5 / viewScale} vectorEffect="non-scaling-stroke" />
        <circle cx={b.nx * W} cy={b.ny * H} r={12 / viewScale} fill={ORANGE} stroke="#fff" strokeWidth={2.5 / viewScale} vectorEffect="non-scaling-stroke" />
        {ft != null && (
          <g style={{ transform: `translate(${midX}px, ${midY - 20 / viewScale}px)` }}>
            <text x={0} y={0} textAnchor="middle" dominantBaseline="middle" fontFamily="ui-monospace,Menlo,monospace" fontSize={18 / viewScale} fontWeight="800" fill={ORANGE} stroke="#ffffff" strokeWidth={5 / viewScale} strokeLinejoin="round" paintOrder="stroke" style={{ paintOrder: "stroke" }}>
              {`${prefix}${formatLength(ft, unit)}`}
            </text>
          </g>
        )}
      </svg>
    );
  }

  // -------------------- COORDINATE MODE --------------------
  if (coordinateMode) {
    // Single tap → crosshair + accuracy ring. Render only the latest point.
    const p = points[points.length - 1];
    if (!p) return null;
    return (
      <svg
        className="absolute inset-0 pointer-events-none"
        viewBox={`0 0 ${W} ${H}`}
        preserveAspectRatio="none"
        data-testid="measure-svg"
      >
        <circle cx={p.nx * W} cy={p.ny * H} r={9 / viewScale} fill="none" stroke={ORANGE} strokeWidth={1 / viewScale} vectorEffect="non-scaling-stroke" />
        <line x1={p.nx * W - 12 / viewScale} y1={p.ny * H} x2={p.nx * W + 12 / viewScale} y2={p.ny * H} stroke={ORANGE} strokeWidth={1 / viewScale} vectorEffect="non-scaling-stroke" />
        <line x1={p.nx * W} y1={p.ny * H - 12 / viewScale} x2={p.nx * W} y2={p.ny * H + 12 / viewScale} stroke={ORANGE} strokeWidth={1 / viewScale} vectorEffect="non-scaling-stroke" />
        <circle cx={p.nx * W} cy={p.ny * H} r={2.5 / viewScale} fill={ORANGE} />
      </svg>
    );
  }

  // -------------------- POLYLINE MODE --------------------
  if (polylineMode) {
    const all = hoverPoint ? [...points, hoverPoint] : points;
    if (all.length === 0) return null;
    const pathD = all.map((p, i) => `${i === 0 ? "M" : "L"} ${p.nx * W} ${p.ny * H}`).join(" ");
    const ft = polylineLengthFt(scale, points);
    // Anchor the live label near the LAST committed point so it doesn't
    // chase the cursor away from the eye line.
    const last = points[points.length - 1];
    const labelXY = last ? { x: last.nx * W, y: last.ny * H - 24 / viewScale } : null;
    return (
      <svg
        className="absolute inset-0 pointer-events-none"
        viewBox={`0 0 ${W} ${H}`}
        preserveAspectRatio="none"
        data-testid="measure-svg"
      >
        {/* Glow underlay — Iter EXP-1 sunlight-readable */}
        <path d={pathD} stroke={ORANGE} strokeOpacity="0.35" strokeWidth={14 / viewScale} fill="none" strokeLinejoin="round" strokeLinecap="round" vectorEffect="non-scaling-stroke" />
        {/* Crisp line */}
        <path d={pathD} stroke={ORANGE} strokeWidth={4 / viewScale} fill="none" strokeLinejoin="round" strokeLinecap="round" vectorEffect="non-scaling-stroke" />
        {/* Vertices — Iter EXP-1 glove-sized */}
        {points.map((p, i) => (
          <g key={i}>
            <circle cx={p.nx * W} cy={p.ny * H} r={22 / viewScale} fill={ORANGE} fillOpacity="0.18" />
            <circle cx={p.nx * W} cy={p.ny * H} r={12 / viewScale} fill={ORANGE} stroke="#fff" strokeWidth={2.5 / viewScale} vectorEffect="non-scaling-stroke" />
          </g>
        ))}
        {/* Live length label — Bluebeam-style halo text, no black chip */}
        {ft != null && labelXY && (
          <g style={{ transform: `translate(${labelXY.x}px, ${labelXY.y}px)` }}>
            <text
              x={0} y={0}
              textAnchor="middle" dominantBaseline="middle"
              fontFamily="ui-monospace,Menlo,monospace"
              fontSize={18 / viewScale}
              fontWeight="800"
              fill={ORANGE}
              stroke="#ffffff"
              strokeWidth={5 / viewScale}
              strokeLinejoin="round"
              paintOrder="stroke"
              style={{ paintOrder: "stroke" }}
            >
              {formatFeet(ft)}
            </text>
          </g>
        )}
      </svg>
    );
  }

  // -------------------- AREA POLYGON MODE --------------------
  if (areaMode) {
    const all = hoverPoint && points.length >= 1 ? [...points, hoverPoint] : points;
    if (all.length === 0) return null;
    const pathD = all.map((p, i) => `${i === 0 ? "M" : "L"} ${p.nx * W} ${p.ny * H}`).join(" ");
    const isClosed = points.length >= 3;
    const closedD = isClosed ? `${pathD} Z` : pathD;
    const sf = isClosed ? polygonAreaSf(scale, points) : null;
    const labelXY = polygonCentroid(points, W, H);
    return (
      <svg
        className="absolute inset-0 pointer-events-none"
        viewBox={`0 0 ${W} ${H}`}
        preserveAspectRatio="none"
        data-testid="measure-svg"
      >
        {/* Filled polygon (with hover preview) — Iter EXP-1 stronger fill */}
        {all.length >= 3 && (
          <path
            d={closedD}
            fill={ORANGE}
            fillOpacity="0.18"
          />
        )}
        {/* Edge line with glow — Iter EXP-1 sunlight-readable */}
        <path
          d={pathD}
          stroke={ORANGE} strokeOpacity="0.35"
          strokeWidth={14 / viewScale}
          fill="none" strokeLinejoin="round" strokeLinecap="round"
          vectorEffect="non-scaling-stroke"
        />
        <path
          d={isClosed ? closedD : pathD}
          stroke={ORANGE}
          strokeWidth={4 / viewScale}
          fill="none" strokeLinejoin="round" strokeLinecap="round"
          vectorEffect="non-scaling-stroke"
        />
        {/* Vertices */}
        {points.map((p, i) => (
          <g key={i}>
            {/* Larger hit zone for dragging */}
            {onVertexDrag && (
              <circle
                cx={p.nx * W} cy={p.ny * H}
                r={26 / viewScale}
                fill="transparent"
                style={{ pointerEvents: "auto", cursor: "grab" }}
                data-vertex-index={i}
              />
            )}
            <circle cx={p.nx * W} cy={p.ny * H} r={22 / viewScale} fill={ORANGE} fillOpacity="0.18" />
            <circle cx={p.nx * W} cy={p.ny * H} r={12 / viewScale} fill={ORANGE} stroke="#fff" strokeWidth={2.5 / viewScale} vectorEffect="non-scaling-stroke" />
          </g>
        ))}
        {/* Area label — Bluebeam-style halo text, no black chip */}
        {sf != null && labelXY && (
          <g style={{ transform: `translate(${labelXY.x}px, ${labelXY.y}px)` }}>
            <text
              x={0} y={0}
              textAnchor="middle" dominantBaseline="middle"
              fontFamily="ui-monospace,Menlo,monospace"
              fontSize={18 / viewScale}
              fontWeight="800"
              fill={ORANGE}
              stroke="#ffffff"
              strokeWidth={5 / viewScale}
              strokeLinejoin="round"
              paintOrder="stroke"
              style={{ paintOrder: "stroke" }}
            >
              {formatArea(sf)}
            </text>
          </g>
        )}
      </svg>
    );
  }

  // -------------------- DISTANCE MODE (existing) --------------------
  const all = hoverPoint ? [...points, hoverPoint] : points;
  if (all.length < 2 && !calibrationMode) return null;
  const lineProps = {
    stroke: ORANGE,
    strokeWidth: 4 / viewScale,
    fill: "none",
    vectorEffect: "non-scaling-stroke",
  };
  // Single segment for now (two-point measurement).
  const a = all[0];
  const b = all[1];
  if (!b) {
    return (
      <svg className="absolute inset-0 pointer-events-none" viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" data-testid="measure-svg">
        <circle cx={a.nx * W} cy={a.ny * H} r={22 / viewScale} fill={ORANGE} fillOpacity="0.18" />
        <circle cx={a.nx * W} cy={a.ny * H} r={12 / viewScale} fill={ORANGE} stroke="#fff" strokeWidth={2.5 / viewScale} vectorEffect="non-scaling-stroke" />
      </svg>
    );
  }

  const dx = (b.nx - a.nx) * W;
  const dy = (b.ny - a.ny) * H;
  const len = Math.hypot(dx, dy);
  const midX = (a.nx + b.nx) / 2 * W;
  const midY = (a.ny + b.ny) / 2 * H;
  // Label offset perpendicular to the line so it doesn't sit on top of it.
  const perpX = len > 0 ? (-dy / len) * (14 / viewScale) : 0;
  const perpY = len > 0 ? (dx / len) * (14 / viewScale) : 0;
  const labelX = midX + perpX;
  const labelY = midY + perpY;

  const distNorm = Math.hypot(b.nx - a.nx, b.ny - a.ny);
  const ft = distanceFt(scale, a, b);
  const label = calibrationMode
    ? "Tap a known distance"
    : (ft != null ? formatFeet(ft) : `${(distNorm * 100).toFixed(1)}% sheet`);

  return (
    <svg
      className="absolute inset-0 pointer-events-none"
      viewBox={`0 0 ${W} ${H}`}
      preserveAspectRatio="none"
      data-testid="measure-svg"
    >
      {/* Glow underlay — Iter EXP-1: thick halo so the line is visible in sunlight glare. */}
      <line
        x1={a.nx * W} y1={a.ny * H}
        x2={b.nx * W} y2={b.ny * H}
        stroke={ORANGE}
        strokeOpacity="0.35"
        strokeWidth={14 / viewScale}
        strokeLinecap="round"
        vectorEffect="non-scaling-stroke"
      />
      {/* Crisp line */}
      <line
        x1={a.nx * W} y1={a.ny * H}
        x2={b.nx * W} y2={b.ny * H}
        {...lineProps}
        strokeLinecap="round"
      />
      {/* Endpoint anchors — Iter EXP-1: glove-sized (r=22 halo + r=12 fill). */}
      <circle cx={a.nx * W} cy={a.ny * H} r={22 / viewScale} fill={ORANGE} fillOpacity="0.18" />
      <circle cx={a.nx * W} cy={a.ny * H} r={12 / viewScale} fill={ORANGE} stroke="#fff" strokeWidth={2.5 / viewScale} vectorEffect="non-scaling-stroke" />
      <circle cx={b.nx * W} cy={b.ny * H} r={22 / viewScale} fill={ORANGE} fillOpacity="0.18" />
      <circle cx={b.nx * W} cy={b.ny * H} r={12 / viewScale} fill={ORANGE} stroke="#fff" strokeWidth={2.5 / viewScale} vectorEffect="non-scaling-stroke" />
      {/* Tick marks */}
      <line
        x1={a.nx * W - perpY * 0.5} y1={a.ny * H + perpX * 0.5}
        x2={a.nx * W + perpY * 0.5} y2={a.ny * H - perpX * 0.5}
        {...lineProps}
      />
      <line
        x1={b.nx * W - perpY * 0.5} y1={b.ny * H + perpX * 0.5}
        x2={b.nx * W + perpY * 0.5} y2={b.ny * H - perpX * 0.5}
        {...lineProps}
      />
      {/* Distance label — Bluebeam-style halo text. No black chip. The
          pulse animation still fires the moment B lands so the operator
          gets the lock-in confirmation, but the visual is a plan-grade
          annotation, not a UI banner. */}
      <g
        className="fop-measure-lock-pulse"
        style={{ transform: `translate(${labelX}px, ${labelY}px)`, transformOrigin: "center" }}
        key={`${a.nx.toFixed(4)}-${a.ny.toFixed(4)}-${b.nx.toFixed(4)}-${b.ny.toFixed(4)}`}
      >
        <text
          x={0} y={0}
          textAnchor="middle"
          dominantBaseline="middle"
          fontFamily="ui-monospace,Menlo,monospace"
          fontSize={18 / viewScale}
          fontWeight="800"
          fill={ORANGE}
          stroke="#ffffff"
          strokeWidth={5 / viewScale}
          strokeLinejoin="round"
          paintOrder="stroke"
          style={{ paintOrder: "stroke" }}
        >
          {label}
        </text>
      </g>
    </svg>
  );
}

/* ============================================================
 * MeasureControls — Iter 33: clean 4-mode tool chooser.
 *
 * One bottom-floating bar. Four icons. A live value display. A close
 * button. Calibration UI surfaces inline only when the user needs it.
 * No "Reset", no "Recalibrate", no nested settings. Drunk-superintendent
 * test compliant.
 * ============================================================ */
export default function MeasureControls({
  active, onClose,
  toolMode, // "distance" | "polyline" | "area" | "coordinate" | "radius" | "diameter" | "angle" | "arc" | "count"
  onSetToolMode,
  points, hoverPoint,
  scale, onCalibrateSave, onClearScale,
  gpsReady,                  // boolean — true when sheetToGps is callable
  onStartCalibration, onStartMeasure, onClearPoints, onUndoPoint,
  calibrationMode,
  onFinishCount,
  imgDims, // Wave B · Turn 1 real-plan cert fix — needed for the angle live readout (see lib/sheetScale::angleDegrees)
  // Connected-workflow program (2026-09-10) — detail-region calibration.
  // When set, the operator drew a bbox first (a separate "Detail Region"
  // step in SheetViewer) and this SAME 2-tap calibration flow now
  // creates a REGION-scoped scale instead of overwriting the sheet's
  // global one. `region_label` is required (the backend rejects a blank
  // one) since it is how the operator later recognises which detail a
  // saved region calibration belongs to.
  regionBbox = null,
  onRegionCalibrateSave,
  onCancelRegion,
  onStartRegionBox,
  regions = [],
  onRenameRegion,
  onDeleteRegion,
  regionBoxMode = false,
  // GH-22 (real-plan certification, 2026-10-01) — needed so the "Using: …"
  // readout agrees with the actual resolved scale (see below) instead of
  // a naive bbox-only guess that can name the wrong sibling region on an
  // anisotropic (plan/profile) sheet.
  scaleContext = null,
}) {
  const [showCalForm, setShowCalForm] = useState(false);
  const [calFeet, setCalFeet] = useState("");
  const [calLabel, setCalLabel] = useState("");
  const [busy, setBusy] = useState(false);
  const [showRegionsList, setShowRegionsList] = useState(false);
  const [renamingRegion, setRenamingRegion] = useState(null); // {id, value}
  const { lengthUnit, setLengthUnit } = useUnitPreference();

  // When both calibration points are picked, surface the "enter distance" form.
  useEffect(() => {
    if (calibrationMode && points?.length === 2) {
      setShowCalForm(true);
    } else if (!calibrationMode) {
      setShowCalForm(false);
    }
  }, [calibrationMode, points]);

  if (!active) return null;

  const calibrated = isCalibrated(scale);
  const isArea = toolMode === "area" && !calibrationMode && !regionBoxMode;
  const isPolyline = toolMode === "polyline" && !calibrationMode && !regionBoxMode;
  const isCoord = toolMode === "coordinate" && !calibrationMode && !regionBoxMode;
  const isRadius = toolMode === "radius" && !calibrationMode && !regionBoxMode;
  const isDiameter = toolMode === "diameter" && !calibrationMode && !regionBoxMode;
  const isAngle = toolMode === "angle" && !calibrationMode && !regionBoxMode;
  const isArc = toolMode === "arc" && !calibrationMode && !regionBoxMode;
  const isCount = toolMode === "count" && !calibrationMode && !regionBoxMode;
  const isDistance = (toolMode === "distance" || calibrationMode) && !regionBoxMode && !isArea && !isPolyline && !isCoord
    && !isRadius && !isDiameter && !isAngle && !isArc && !isCount;
  // LOW fix (real-plan cert iteration_184) — distance/radius/diameter had
  // no live readout while rubber-banding to point B (polyline/area/arc
  // already showed one via hoverPoint); now shows a running value as
  // soon as point A is placed and the cursor/finger is hovering.
  const liveDistTarget = points?.length === 2 ? points[1] : (points?.length === 1 ? hoverPoint : null);
  const liveDist = (isDistance || isRadius || isDiameter) && points?.length >= 1 && liveDistTarget
    ? distanceFt(scale, points[0], liveDistTarget)
    : null;
  const liveArea = isArea && points?.length >= 3 ? polygonAreaSf(scale, points) : null;
  const livePoly = isPolyline && points?.length >= 2 ? polylineLengthFt(scale, points) : null;
  const liveArcLen = isArc && points?.length >= 3 ? arcLengthFt(scale, points) : null;
  const liveAngle = isAngle && points?.length === 3 ? angleDegrees(points, imgDims) : null;

  const saveCalibration = async () => {
    if (points?.length !== 2) return;
    const ft = parseFloat(calFeet);
    if (!Number.isFinite(ft) || ft <= 0) return;
    if (regionBbox && !calLabel.trim()) return; // region_label is required server-side
    setBusy(true);
    try {
      // Iter 144 Phase 2 — capture the measured raster distance so we can
      // surface a calibration-confidence toast after the save succeeds.
      const measuredNorm = Math.hypot(
        points[1].nx - points[0].nx,
        points[1].ny - points[0].ny,
      );
      const result = regionBbox
        ? await onRegionCalibrateSave({
            p1_nx: points[0].nx, p1_ny: points[0].ny,
            p2_nx: points[1].nx, p2_ny: points[1].ny,
            real_distance_ft: ft,
            region_bbox: regionBbox,
            region_label: calLabel.trim(),
          })
        : await onCalibrateSave({
            p1_nx: points[0].nx, p1_ny: points[0].ny,
            p2_nx: points[1].nx, p2_ny: points[1].ny,
            real_distance_ft: ft,
            label: calLabel.trim() || null,
          });
      setShowCalForm(false);
      setCalFeet("");
      setCalLabel("");
      onClearPoints();
      // Iter EXP-2A — Calibration completion CONFIDENCE MOMENT.
      // Not "Scale set." A real confirmation card with:
      //   ✓ HIGH CONFIDENCE
      //   Measured 42.7 ft = 100% sheet width
      //   Ready for field positioning
      try {
        const ftPerNorm = ft / Math.max(1e-9, measuredNorm);
        const oneFt = ftPerNorm > 0 ? 1 / ftPerNorm : 0;
        // Confidence heuristic: a measurement line that spans <2% of
        // the sheet diagonal is operator-fragile; ≥10% is solid.
        const confidence = measuredNorm >= 0.10 ? "HIGH"
                          : measuredNorm >= 0.04 ? "GOOD"
                          : "LOW";
        toast.success(
          regionBbox ? `✓ ${confidence} CONFIDENCE · Detail region calibrated` : `✓ ${confidence} CONFIDENCE · Scale calibrated`,
          {
            description: `${ft.toLocaleString()} ft = ${(measuredNorm * 100).toFixed(1)}% of sheet · 1 ft ≈ ${(oneFt * 100).toFixed(2)}% sheet${result?.region_label || result?.label ? ` · ${result.region_label || result.label}` : ""}\nReady for field positioning.`,
            duration: 6500,
          }
        );
      } catch { /* toast failure is silent */ }
      // Drop the user straight into measure mode now that we're calibrated.
      onStartMeasure();
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
    {/* Iter 144 Phase 2 — calibration wizard. Shows clear step pills so the
        operator always knows where they are in the flow. No new clicks; this
        is purely a visual reframe of the existing tap-A → tap-B → enter →
        save sequence. */}
    {calibrationMode && (
      <div
        className="fixed bottom-[64px] left-1/2 -translate-x-1/2 z-[59] flex items-center gap-1.5 px-3 py-2 rounded-full bg-[var(--fop-surface-2)]/95 backdrop-blur border border-[var(--fop-border-hi)] shadow-2xl"
        data-testid="calibration-wizard-steps"
      >
        {regionBbox && (
          <span className="text-[9px] font-mono uppercase tracking-[0.15em] text-[var(--fop-cta-bg)] mr-1" data-testid="calibration-wizard-region-badge">
            REGION ·
          </span>
        )}
        <CalStepPill n={1} label="Tap A"     active={points.length === 0} done={points.length >= 1} />
        <span className="text-[var(--fop-text-3)] text-[10px]">›</span>
        <CalStepPill n={2} label="Tap B"     active={points.length === 1} done={points.length >= 2} />
        <span className="text-[var(--fop-text-3)] text-[10px]">›</span>
        <CalStepPill n={3} label="Distance"  active={points.length === 2 && !calFeet} done={!!calFeet && parseFloat(calFeet) > 0} />
        <span className="text-[var(--fop-text-3)] text-[10px]">›</span>
        <CalStepPill n={4} label="Confirm"   active={!!calFeet && parseFloat(calFeet) > 0} done={false} />
      </div>
    )}
    {/* Connected-workflow program (2026-09-10) — step 1 of Detail Region:
        draw the box (2 opposite-corner taps) before the familiar 2-tap
        distance calibration takes over. */}
    {regionBoxMode && (
      <div
        className="fixed bottom-[64px] left-1/2 -translate-x-1/2 z-[59] flex items-center gap-2 px-3 py-2 rounded-full bg-[var(--fop-surface-2)]/95 backdrop-blur border border-[var(--fop-border-hi)] shadow-2xl"
        data-testid="region-box-wizard-steps"
      >
        <span className="text-[9px] font-mono uppercase tracking-[0.15em] text-[var(--fop-cta-bg)]">REGION BOX ·</span>
        <CalStepPill n={1} label="Corner A" active={points.length === 0} done={points.length >= 1} />
        <span className="text-[var(--fop-text-3)] text-[10px]">›</span>
        <CalStepPill n={2} label="Corner B" active={points.length === 1} done={points.length >= 2} />
        <button
          onClick={onClose}
          className="ml-1 text-[10px] font-mono uppercase tracking-wider text-[var(--fop-text-3)] hover:text-[var(--st-escalated)]"
          data-testid="region-box-cancel"
        >
          Cancel
        </button>
      </div>
    )}
    {showCalForm && (
      <div
        className="fixed bottom-[120px] left-1/2 -translate-x-1/2 z-[60] w-[320px] rounded-lg shadow-2xl border border-[var(--fop-primary)] bg-[var(--fop-surface)] p-4 space-y-3"
        onPointerDown={(e) => e.stopPropagation()}
        onWheel={(e) => e.stopPropagation()}
        data-testid="measure-calibration-form"
      >
        <div className="font-mono text-[11px] uppercase tracking-[0.18em] text-[var(--fop-primary)]">
          {regionBbox ? "Known distance inside this detail region" : "Known distance between the two points"}
        </div>
        {/* Iter 147 discovery — honesty disclaimer. Operators have asked
            where to set "project survey control" expecting this dialog
            to do it. It doesn't. Set Scale sets per-sheet measurement
            scale; it is not a project coordinate-system setup. */}
        <div className="text-[11px] text-[var(--fop-text-3)] leading-snug" data-testid="set-scale-disclaimer">
          {regionBbox
            ? "Sets measurement scale for ONLY this boxed detail. Measurements outside the box keep using the sheet's own scale."
            : "Sets measurement scale for this sheet only. This is "}
          {!regionBbox && <em>not</em>}
          {!regionBbox && " project survey control or benchmark coordinates."}
        </div>
        <div className="flex items-center gap-2">
          <input
            autoFocus
            value={calFeet}
            onChange={(e) => setCalFeet(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") saveCalibration(); }}
            placeholder="e.g. 100"
            className="flex-1 min-h-[44px] text-base py-2 px-3 font-mono"
            data-testid="measure-cal-feet"
            inputMode="decimal"
          />
          <span className="text-sm font-mono text-[var(--fop-text-3)]">feet</span>
        </div>
        <input
          value={calLabel}
          onChange={(e) => setCalLabel(e.target.value)}
          placeholder={regionBbox ? "Region name, e.g. 'Detail A — Inlet structure' (required)" : "Optional: '200 ft graphic scale'"}
          className="w-full min-h-[44px] text-sm py-2 px-3"
          data-testid="measure-cal-label"
        />
        <div className="flex items-center justify-between gap-2 pt-1">
          <button
            onClick={() => { setShowCalForm(false); onClearPoints(); if (regionBbox && onCancelRegion) onCancelRegion(); }}
            className="min-h-[44px] px-3 text-[11px] font-mono uppercase tracking-wider text-[var(--fop-text-3)] hover:text-[var(--fop-text)]"
            data-testid="measure-cal-cancel"
          >
            Cancel
          </button>
          {!regionBbox && scale && isCalibrated(scale) && (
            <button
              onClick={async () => { setBusy(true); try { await onClearScale(); } finally { setBusy(false); setShowCalForm(false); } }}
              className="min-h-[44px] px-3 text-[11px] font-mono uppercase tracking-wider text-[var(--fop-text-3)] hover:text-[var(--st-escalated)] flex items-center gap-1"
              data-testid="measure-cal-clear"
            >
              <Trash2 size={11} /> Clear scale
            </button>
          )}
          <button
            onClick={saveCalibration}
            disabled={busy || !calFeet || parseFloat(calFeet) <= 0 || (!!regionBbox && !calLabel.trim())}
            className="min-h-[44px] px-4 text-[12px] font-mono uppercase tracking-wider rounded bg-[var(--fop-primary)] text-black disabled:opacity-40 flex items-center gap-1.5 font-bold"
            data-testid="measure-cal-save"
          >
            {busy ? <Loader2 size={12} className="animate-spin" /> : <Check size={12} />}
            {regionBbox ? "Save region scale" : "Save scale"}
          </button>
        </div>
      </div>
    )}
    {/* Standalone fixed panel (NOT nested in the overflow-hidden toolbar
        row below — see comment at the Regions toggle button). */}
    {showRegionsList && regions.length > 0 && (
      <div
        className="fixed bottom-[64px] right-4 z-[60] w-64 max-h-64 overflow-y-auto rounded-lg bg-[var(--fop-surface-2)] border border-[var(--fop-border-hi)] shadow-2xl p-1.5"
        onPointerDown={(e) => e.stopPropagation()}
        onWheel={(e) => e.stopPropagation()}
        data-testid="measure-regions-list"
      >
        {regions.map((r) => (
          <div key={r.id} className="flex items-center gap-1.5 px-2 py-1.5 rounded hover:bg-[var(--fop-surface-3)]" data-testid={`region-list-item-${r.id}`}>
            {renamingRegion?.id === r.id ? (
              <>
                <input
                  autoFocus
                  value={renamingRegion.value}
                  onChange={(e) => setRenamingRegion({ id: r.id, value: e.target.value })}
                  className="flex-1 min-w-0 bg-[var(--fop-surface-1)] border border-[var(--fop-border)] rounded px-1.5 py-1 text-[11px] font-mono"
                  data-testid={`region-rename-input-${r.id}`}
                />
                <button
                  onClick={async () => {
                    const val = renamingRegion.value.trim();
                    if (val && onRenameRegion) await onRenameRegion(r.id, val);
                    setRenamingRegion(null);
                  }}
                  data-testid={`region-rename-save-${r.id}`}
                  title="Save name"
                >
                  <Check size={12} className="text-[var(--fop-success)]" />
                </button>
              </>
            ) : (
              <>
                <span className="flex-1 min-w-0 truncate text-[11px] font-mono" data-testid={`region-list-label-${r.id}`}>
                  {r.region_label || r.label || "Region"}
                </span>
                <button
                  onClick={() => setRenamingRegion({ id: r.id, value: r.region_label || r.label || "" })}
                  title="Rename"
                  data-testid={`region-rename-${r.id}`}
                >
                  <ChevronDown size={11} className="rotate-90 text-[var(--fop-text-3)] hover:text-[var(--fop-text)]" />
                </button>
                <button
                  onClick={async () => { if (onDeleteRegion) await onDeleteRegion(r.id); }}
                  title="Delete this region's calibration"
                  data-testid={`region-delete-${r.id}`}
                >
                  <Trash2 size={11} className="text-[var(--fop-text-3)] hover:text-[var(--st-escalated)]" />
                </button>
              </>
            )}
          </div>
        ))}
      </div>
    )}
    <div
      className="fixed bottom-3 left-1/2 -translate-x-1/2 z-[55] rounded-lg shadow-2xl border border-[var(--fop-border-hi)] bg-[var(--fop-surface)] flex items-stretch overflow-hidden"
      data-testid="measure-controls"
      onPointerDown={(e) => e.stopPropagation()}
      onWheel={(e) => e.stopPropagation()}
    >
      {/* Tool chooser — 4 primary modes + a "More" popover for the 5
          Wave B · Turn 1 kinds (radius/diameter/angle/arc/count), so the
          primary bar stays uncluttered on a dense real plan sheet. */}
      {!calibrationMode && (
        <div className="flex items-stretch border-r border-[var(--fop-border)] relative">
          <ToolBtn active={toolMode === "distance"}  onClick={() => { onSetToolMode("distance"); }}  testid="measure-mode-distance" label="Distance"  Icon={Ruler}/>
          <ToolBtn active={toolMode === "polyline"}  onClick={() => { onSetToolMode("polyline"); }}  testid="measure-mode-polyline" label="Polyline"  Icon={Spline}/>
          <ToolBtn active={toolMode === "area"}      onClick={() => { onSetToolMode("area"); }}      testid="measure-mode-area"     label="Area"      Icon={Hexagon}/>
          <ToolBtn active={toolMode === "coordinate"} onClick={() => { onSetToolMode("coordinate"); }} testid="measure-mode-coordinate" label="Coord" Icon={MapPin}/>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <ToolBtn
                active={isRadius || isDiameter || isAngle || isArc || isCount}
                onClick={() => {}}
                testid="measure-mode-more"
                label="More"
                Icon={ChevronDown}
              />
            </DropdownMenuTrigger>
            <DropdownMenuContent side="top" align="start" className="p-1 min-w-[140px]" data-testid="measure-more-menu">
              <DropdownMenuItem data-testid="measure-mode-radius" onSelect={() => onSetToolMode("radius")} className="gap-2 cursor-pointer">
                <Circle className="w-3.5 h-3.5" /> Radius
              </DropdownMenuItem>
              <DropdownMenuItem data-testid="measure-mode-diameter" onSelect={() => onSetToolMode("diameter")} className="gap-2 cursor-pointer">
                <CircleDot className="w-3.5 h-3.5" /> Diameter
              </DropdownMenuItem>
              <DropdownMenuItem data-testid="measure-mode-angle" onSelect={() => onSetToolMode("angle")} className="gap-2 cursor-pointer">
                <Triangle className="w-3.5 h-3.5" /> Angle
              </DropdownMenuItem>
              <DropdownMenuItem data-testid="measure-mode-arc" onSelect={() => onSetToolMode("arc")} className="gap-2 cursor-pointer">
                <Waypoints className="w-3.5 h-3.5" /> Arc
              </DropdownMenuItem>
              <DropdownMenuItem data-testid="measure-mode-count" onSelect={() => onSetToolMode("count")} className="gap-2 cursor-pointer">
                <Hash className="w-3.5 h-3.5" /> Count
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      )}

      {/* Live value display — Iter EXP-1 promoted readout. */}
      <div className="flex items-center gap-2 px-4 py-2 min-w-[220px]">
        <div className="flex-1">
          <div className="font-mono text-[11px] uppercase tracking-[0.18em] text-[var(--fop-text-2)]">
            {calibrationMode ? "Calibrate scale"
              : isCoord ? "Coordinate"
              : isPolyline ? "Polyline"
              : isArea ? "Area"
              : isRadius ? "Radius"
              : isDiameter ? "Diameter"
              : isAngle ? "Angle"
              : isArc ? "Arc"
              : isCount ? "Count"
              : "Distance"}
          </div>
          <div className="text-[16px] text-[var(--fop-text)] font-mono font-bold leading-tight" data-testid="measure-readout">
            {calibrationMode
              ? (points.length === 0 ? "Tap point A"
                  : points.length === 1 ? "Tap point B"
                  : "Enter distance →")
              : isCoord
                ? (gpsReady
                    ? (points.length === 0 ? "Tap on the drawing" : "Saved")
                    : "no GPS calibration")
              : isPolyline
                ? (points.length === 0 ? "Tap first point"
                    : points.length === 1 ? "Tap next point"
                    : livePoly != null ? `${formatLength(livePoly, lengthUnit)} · double-tap to finish`
                    : `${points.length} points · double-tap to finish`)
              : isArea
                ? (points.length === 0 ? "Tap first vertex"
                    : points.length < 3 ? `Tap vertex (${points.length}/3+)`
                    : liveArea != null ? formatAreaValue(liveArea, lengthUnit === "m" ? "sm" : "sf")
                    : "Tap next vertex…")
              : isRadius || isDiameter
                ? (points.length === 2 ? (formatLength(liveDist, lengthUnit) || "—")
                    : points.length === 1 ? (liveDist != null ? `${formatLength(liveDist, lengthUnit)} · tap to set` : "Tap the edge point")
                    : `Tap the ${isRadius ? "center" : "first edge"} point`)
              : isAngle
                ? (points.length === 0 ? "Tap point A"
                    : points.length === 1 ? "Tap the vertex"
                    : points.length === 2 ? "Tap point B"
                    : liveAngle != null ? `${liveAngle.toFixed(1)}\u00b0` : "—")
              : isArc
                ? (points.length === 0 ? "Tap first point"
                    : points.length < 3 ? `Tap point (${points.length}/3+)`
                    : liveArcLen != null ? `${formatLength(liveArcLen, lengthUnit)} · tap last point again to finish`
                    : `${points.length} points · tap last point again to finish`)
              : isCount
                ? (points.length === 0 ? "Tap each item"
                    : `${points.length} item${points.length === 1 ? "" : "s"} · tap Finish when done`)
              : (points.length === 2 ? (formatLength(liveDist, lengthUnit) || "—")
                  : points.length === 1 ? (liveDist != null ? `${formatLength(liveDist, lengthUnit)} · tap to set` : "Tap point B")
                  : "Tap point A")}
          </div>
          {/* Connected-workflow program (2026-09-10) — show the
              applicable scale WHILE measuring, not just discoverable
              afterward in history.
              GH-22 (real-plan certification, 2026-10-01) — this used to
              be a naive "does the hover point's bbox contain this?"
              check, which could name the WRONG sibling region on an
              anisotropic (plan/profile) sheet carrying two region
              calibrations over the same box (one per axis). Route
              through the same `resolveScaleForPoints` the live value
              and the backend both use so the label never contradicts
              the number shown next to it. */}
          {!calibrationMode && !regionBoxMode && regions.length > 0 && !isCoord && !isAngle && !isCount && (() => {
            const probe = hoverPoint ? [...points, hoverPoint] : points;
            if (!probe.length) return null;
            const resolved = resolveScaleForPoints(regions, probe, scaleContext);
            const hit = resolved.scale && resolved.scale.region_bbox ? resolved.scale : null;
            return (
              <div className="mt-0.5" data-testid="measure-applicable-scale">
                <span className="text-[9px] font-mono uppercase tracking-[0.14em] text-[var(--fop-text-3)]">
                  Using: {hit ? (hit.region_label || hit.label || "Region") : "Sheet scale"}
                  {resolved.trust === "unverified" || resolved.trust === "untrusted" ? " (unverified)" : ""}
                </span>
              </div>
            );
          })()}
        </div>
      </div>

      {/* Wave B · Turn 1 — unit picker. Cycles ft (Intl) → US Survey Ft →
          Meters, always visible so the project's active unit is never a
          guessing game. */}
      {!calibrationMode && (
        <button
          onClick={() => {
            const idx = LENGTH_UNIT_CHOICES.findIndex((u) => u.id === lengthUnit);
            const next = LENGTH_UNIT_CHOICES[(idx + 1) % LENGTH_UNIT_CHOICES.length];
            setLengthUnit(next.id);
          }}
          className="px-2.5 py-2 text-[11px] font-mono uppercase tracking-wider text-[var(--fop-text-2)] hover:text-[var(--fop-primary)] hover:bg-[var(--fop-surface-2)] border-l border-[var(--fop-border)] flex items-center gap-1"
          data-testid="measure-unit-picker"
          title="Cycle measurement units"
        >
          <span data-testid="measure-unit-current">{LENGTH_UNIT_CHOICES.find((u) => u.id === lengthUnit)?.short || "FT"}</span>
        </button>
      )}

      {/* Inline calibration nudge — only shown when the tool actually
          NEEDS calibration and the sheet isn't set up yet. For coordinate
          mode that's GPS readiness; angle/count are scale-independent. */}
      {!calibrationMode && !regionBoxMode && !isCoord && !isAngle && !isCount && !calibrated && (
        <button
          onClick={onStartCalibration}
          className="px-2.5 py-2 text-[11px] font-mono uppercase tracking-wider text-[var(--fop-primary)] hover:bg-[var(--fop-surface-2)] border-l border-[var(--fop-border)] flex items-center gap-1.5"
          data-testid="measure-set-scale"
          title="Set the sheet scale for measurements (not project survey control)"
        >
          <Crosshair size={11} />
          Set scale
        </button>
      )}

      {/* Connected-workflow program (2026-09-10) — Detail Region
          calibration. Independent of the sheet's global scale (which
          may or may not be set), so this is always available, not
          gated on `!calibrated`. Draws a box, THEN reuses the exact
          same 2-tap calibration flow scoped to it. */}
      {!calibrationMode && !regionBoxMode && !isCoord && !isAngle && !isCount && onStartRegionBox && (
        <button
          onClick={onStartRegionBox}
          className="px-2.5 py-2 text-[11px] font-mono uppercase tracking-wider text-[var(--fop-text-2)] hover:text-[var(--fop-primary)] hover:bg-[var(--fop-surface-2)] border-l border-[var(--fop-border)] flex items-center gap-1.5"
          data-testid="measure-detail-region"
          title="Calibrate a detail region — a boxed area of this sheet with its own scale"
        >
          <ScanLine size={11} />
          Detail region
        </button>
      )}

      {/* Regions list — inspect / rename / remove existing detail-region
          calibrations. Only shown once at least one exists, so sheets
          with a single global scale (the common case) see no extra
          chrome. Toggle button lives inline in the toolbar; the actual
          dropdown panel is rendered as a standalone `fixed` sibling
          further down (NOT nested here) — the toolbar row has
          `overflow-hidden` (for its rounded-pill corners), which was
          silently clipping/hit-test-blocking an absolutely-positioned
          popup nested inside it (testing-agent iteration_263 HIGH
          finding: real clicks on rename/delete were swallowed by the
          canvas underneath). */}
      {!calibrationMode && !regionBoxMode && regions.length > 0 && (
        <button
          onClick={() => setShowRegionsList((v) => !v)}
          className="px-2.5 py-2 text-[11px] font-mono uppercase tracking-wider text-[var(--fop-text-2)] hover:text-[var(--fop-primary)] hover:bg-[var(--fop-surface-2)] border-l border-[var(--fop-border)] flex items-center gap-1.5"
          data-testid="measure-regions-toggle"
          title="View and manage detail-region calibrations"
        >
          <ChevronDown size={11} className={showRegionsList ? "rotate-180 transition-transform" : "transition-transform"} />
          Regions ({regions.length})
        </button>
      )}

      {/* Count tool — explicit finish (no proximity-finish gesture since
          taps are scattered, not sequential). */}
      {isCount && points.length > 0 && (
        <button
          onClick={onFinishCount}
          className="min-h-[44px] px-3 py-2 text-[11px] font-mono uppercase tracking-wider text-[var(--fop-primary)] hover:bg-[var(--fop-surface-2)] border-l border-[var(--fop-border)] flex items-center gap-1.5"
          data-testid="measure-finish-count"
        >
          <Flag size={12} /> Finish ({points.length})
        </button>
      )}

      {/* Iter 144 Phase 2 — VISIBLE ACTION CONTROLS (#6).
          Undo / Restart / Cancel are always reachable while measuring or
          calibrating. No hidden menus. */}
      {points.length > 0 && (
        <button
          onClick={onUndoPoint}
          className="min-h-[44px] px-3 py-2 text-[10px] font-mono uppercase tracking-wider text-[var(--fop-text-2)] hover:text-[var(--fop-text)] hover:bg-[var(--fop-surface-2)] border-l border-[var(--fop-border)] flex items-center gap-1.5"
          data-testid="measure-undo"
          title="Undo last point (Z)"
          aria-label="Undo last point"
        >
          <Undo2 size={13} /> Undo
        </button>
      )}
      {points.length > 0 && (
        <button
          onClick={onClearPoints}
          className="min-h-[44px] px-3 py-2 text-[10px] font-mono uppercase tracking-wider text-[var(--fop-text-2)] hover:text-[var(--fop-text)] hover:bg-[var(--fop-surface-2)] border-l border-[var(--fop-border)] flex items-center gap-1.5"
          data-testid="measure-restart"
          title="Clear points, stay in this tool"
          aria-label="Restart measurement"
        >
          <RotateCcw size={13} /> Restart
        </button>
      )}

      <button
        onClick={onClose}
        className="min-h-[44px] px-3 py-2 text-[10px] font-mono uppercase tracking-wider text-[var(--fop-text-3)] hover:text-[var(--st-escalated)] border-l border-[var(--fop-border)] flex items-center gap-1.5"
        data-testid="measure-close"
        title="Cancel — close the tool (Esc)"
        aria-label="Cancel measure tool"
      >
        <X size={13} /> Cancel
      </button>
    </div>
    </>
  );
}


/** Compact tool button used in the chooser. Tactile but quiet. */
const ToolBtn = React.forwardRef(function ToolBtn({ active, onClick, testid, label, Icon, ...rest }, ref) {
  return (
    <button
      ref={ref}
      onClick={onClick}
      className={`min-h-[44px] px-3 py-2 text-[11px] font-mono uppercase tracking-wider flex flex-col items-center gap-0.5 transition-colors ${
        active
          ? "bg-[var(--fop-primary-soft)] text-[var(--fop-primary)]"
          : "text-[var(--fop-text-3)] hover:text-[var(--fop-text)]"
      }`}
      data-testid={testid}
      title={label}
      aria-pressed={active}
      {...rest}
    >
      <Icon size={14} />
      <span className="text-[8.5px] tracking-wider">{label}</span>
    </button>
  );
});

/** Iter 144 Phase 2 — calibration wizard step pill. */
function CalStepPill({ n, label, active, done }) {
  const tone = done
    ? "text-[var(--fop-primary)] border-[var(--fop-primary)] bg-[var(--fop-primary-soft)]"
    : active
    ? "text-[var(--fop-text)] border-[var(--fop-border-hi)] bg-[var(--fop-surface)]"
    : "text-[var(--fop-text-3)] border-[var(--fop-border)] bg-transparent";
  return (
    <span
      className={`inline-flex items-center gap-1 px-2 py-1 rounded-full border font-mono text-[10px] uppercase tracking-wider ${tone}`}
      data-testid={`cal-step-${n}`}
      data-state={done ? "done" : active ? "active" : "pending"}
    >
      <span className="inline-flex w-4 h-4 items-center justify-center rounded-full bg-[var(--fop-surface-2)] text-[9px] font-bold">
        {done ? <Check size={9} /> : n}
      </span>
      {label}
    </span>
  );
}


/* ============================================================
 * MeasurementsLayer — Iter 33 persistent overlay.
 *
 * Renders all saved measurements for the current sheet as a quiet,
 * read-only layer underneath the live measure-in-progress overlay.
 * Click a measurement → delete (one-tap with a brief hover hint).
 *
 * Props:
 *   sheetId     — current sheet (drives the GET /measurements fetch)
 *   imgDims     — natural raster dims for SVG viewBox
 *   viewScale   — current canvas zoom multiplier (for non-scaling stroke math)
 *   reloadKey   — opaque value the parent bumps after auto-save so this
 *                 layer refetches and renders the new row immediately
 *   onDelete()  — fired after a measurement is successfully deleted, so
 *                 the parent can bump reloadKey or re-render
 */
/* MEDIUM fix (real-plan cert iteration_183) — overlapping measurement
 * labels became illegible on dense real plans (three distance labels on
 * one segment rendered as an unreadable mash). Compute each
 * measurement's natural label anchor first, then nudge any that land
 * within a collision radius of an already-placed label straight down in
 * fixed steps so they stack legibly instead of overlapping. Both the
 * dedup pass and the renderer use this SAME anchor function so the
 * stacking offset is the only difference from the natural position. */
function _baseLabelXY(measurement, W, H, viewScale) {
  const pts = measurement.points || [];
  if (!pts.length) return null;
  if (measurement.kind === "coordinate" || measurement.kind === "count") {
    const p = pts[0];
    return { x: p.nx * W, y: p.ny * H - 14 / viewScale };
  }
  if (measurement.kind === "area") {
    let A = 0, cx = 0, cy = 0;
    for (let i = 0; i < pts.length; i++) {
      const p = pts[i], q = pts[(i + 1) % pts.length];
      const cross = p.nx * q.ny - q.nx * p.ny;
      A += cross;
      cx += (p.nx + q.nx) * cross;
      cy += (p.ny + q.ny) * cross;
    }
    return Math.abs(A) < 1e-9
      ? { x: pts[0].nx * W, y: pts[0].ny * H }
      : { x: (cx / (3 * A)) * W, y: (cy / (3 * A)) * H };
  }
  if (measurement.kind === "distance" || measurement.kind === "radius" || measurement.kind === "diameter") {
    const a = pts[0], b = pts[1] || pts[0];
    return { x: ((a.nx + b.nx) / 2) * W, y: ((a.ny + b.ny) / 2) * H - 14 / viewScale };
  }
  if (measurement.kind === "angle" && pts[1]) {
    return { x: pts[1].nx * W, y: pts[1].ny * H - 20 / viewScale };
  }
  const last = pts[pts.length - 1];
  return { x: last.nx * W, y: last.ny * H - 14 / viewScale };
}

function _deconflictLabels(rows, W, H, viewScale) {
  const CLUSTER_PX = 46 / viewScale;
  const STEP_PX = 20 / viewScale;
  const placed = [];
  const out = {};
  for (const m of rows) {
    const base = _baseLabelXY(m, W, H, viewScale);
    if (!base) continue;
    let { x, y } = base;
    let bump = 0;
    while (placed.some((q) => Math.hypot(q.x - x, q.y - y) < CLUSTER_PX) && bump < 12) {
      bump += 1;
      y = base.y + bump * STEP_PX;
    }
    placed.push({ x, y });
    out[m.id] = { x, y };
  }
  return out;
}

export function MeasurementsLayer({ sheetId, imgDims, viewScale, reloadKey, onDelete }) {
  const [rows, setRows] = useState([]);
  const W = imgDims?.w || 1;
  const H = imgDims?.h || 1;

  useEffect(() => {
    if (!sheetId) { setRows([]); return; }
    let cancel = false;
    api.get(`/sheets/${sheetId}/measurements`)
      .then((r) => { if (!cancel) setRows(r.data || []); })
      .catch(() => { if (!cancel) setRows([]); });
    return () => { cancel = true; };
  }, [sheetId, reloadKey]);

  const handleDelete = async (id) => {
    try {
      await api.delete(`/measurements/${id}`);
      setRows((prev) => prev.filter((r) => r.id !== id));
      onDelete?.();
    } catch {/* silent */}
  };

  if (!rows.length) return null;

  const labelPositions = _deconflictLabels(rows, W, H, viewScale);

  return (
    <svg
      className="absolute inset-0 pointer-events-none"
      viewBox={`0 0 ${W} ${H}`}
      preserveAspectRatio="none"
      data-testid="measurements-layer"
      data-count={rows.length}
    >
      {rows.map((m) => (
        <SavedMeasurement
          key={m.id}
          measurement={m}
          W={W} H={H}
          viewScale={viewScale}
          onDelete={handleDelete}
          labelXY={labelPositions[m.id]}
        />
      ))}
    </svg>
  );
}


function SavedMeasurement({ measurement, W, H, viewScale, onDelete, labelXY }) {
  // Each saved measurement renders muted by default (gray-orange) and
  // brightens on hover. Tap the label → delete.
  const muted = "rgba(232, 93, 31, 0.55)";
  const dim = "rgba(255, 255, 255, 0.35)";
  const pts = measurement.points || [];
  if (!pts.length) return null;

  if (measurement.kind === "coordinate") {
    const p = pts[0];
    const lxy = labelXY || { x: p.nx * W, y: p.ny * H - 14 / viewScale };
    return (
      <g
        data-testid={`saved-measurement-${measurement.id}`}
        data-kind="coordinate"
        data-viewer-overlay="1"
        data-overlay-type="measurement-coordinate"
        data-overlay-id={measurement.id}
        data-overlay-anchor-nx={p.nx}
        data-overlay-anchor-ny={p.ny}
      >
        <circle cx={p.nx * W} cy={p.ny * H} r={7 / viewScale} fill="none" stroke={muted} strokeWidth={0.75 / viewScale} vectorEffect="non-scaling-stroke" />
        <circle cx={p.nx * W} cy={p.ny * H} r={2 / viewScale} fill={muted} />
        <LabelChip x={lxy.x} y={lxy.y} text={measurementDisplayLabel(measurement) || "—"} viewScale={viewScale} muted onDelete={() => onDelete(measurement.id)} testid={`measurement-label-${measurement.id}`} />
      </g>
    );
  }

  // Wave B · Turn 1 — count kind: discrete numbered tally markers, no
  // connecting path (points are scattered instances, not a shape).
  // MEDIUM fix (real-plan cert iteration_183) — markers were illegible
  // (r=5, fontSize=4) at typical fit zoom; bumped to match the live
  // in-progress count preview's legibility.
  if (measurement.kind === "count") {
    const anchor0 = pts[0];
    const lxy = labelXY || { x: anchor0.nx * W, y: anchor0.ny * H - 14 / viewScale };
    return (
      <g
        data-testid={`saved-measurement-${measurement.id}`}
        data-kind="count"
        data-viewer-overlay="1"
        data-overlay-type="measurement-count"
        data-overlay-id={measurement.id}
        data-overlay-anchor-nx={anchor0.nx}
        data-overlay-anchor-ny={anchor0.ny}
      >
        {pts.map((p, i) => (
          <g key={i}>
            <circle cx={p.nx * W} cy={p.ny * H} r={9 / viewScale} fill={muted} stroke="#ffffff" strokeWidth={1 / viewScale} vectorEffect="non-scaling-stroke" />
            <text x={p.nx * W} y={p.ny * H} textAnchor="middle" dominantBaseline="middle" fontFamily="ui-monospace,Menlo,monospace" fontSize={7.5 / viewScale} fontWeight="800" fill="#fff" stroke="#000" strokeWidth={0.4 / viewScale} paintOrder="stroke">{i + 1}</text>
          </g>
        ))}
        <LabelChip x={lxy.x} y={lxy.y} text={measurementDisplayLabel(measurement) || `${pts.length} items`} viewScale={viewScale} muted onDelete={() => onDelete(measurement.id)} testid={`measurement-label-${measurement.id}`} />
      </g>
    );
  }

  // distance / polyline / area / radius / diameter / angle / arc share the
  // polyline rendering path (a line through points, optionally closed).
  const isArea = measurement.kind === "area";
  const pathD = pts.map((p, i) => `${i === 0 ? "M" : "L"} ${p.nx * W} ${p.ny * H}`).join(" ");
  const closedD = isArea ? `${pathD} Z` : pathD;
  const labelXYFinal = labelXY || _baseLabelXY(measurement, W, H, viewScale);

  const anchor = pts[0] || { nx: 0, ny: 0 };
  const overlayType =
    measurement.kind === "distance" ? "measurement-line"
      : measurement.kind === "polyline" ? "measurement-polyline"
      : measurement.kind === "area" ? "measurement-polygon"
      : `measurement-${measurement.kind}`;

  return (
    <g
      data-testid={`saved-measurement-${measurement.id}`}
      data-kind={measurement.kind}
      data-viewer-overlay="1"
      data-overlay-type={overlayType}
      data-overlay-id={measurement.id}
      data-overlay-anchor-nx={anchor.nx}
      data-overlay-anchor-ny={anchor.ny}
    >
      {isArea && (
        <path d={closedD} fill={muted} fillOpacity="0.05" />
      )}
      <path d={closedD} stroke={muted} strokeWidth={1 / viewScale} fill="none" strokeLinejoin="round" strokeLinecap="round" vectorEffect="non-scaling-stroke" />
      {pts.map((p, i) => (
        <circle key={i} cx={p.nx * W} cy={p.ny * H} r={3 / viewScale} fill={muted} stroke={dim} strokeWidth={0.5 / viewScale} vectorEffect="non-scaling-stroke" />
      ))}
      {labelXYFinal && (
        <LabelChip x={labelXYFinal.x} y={labelXYFinal.y} text={measurementDisplayLabel(measurement) || "—"} viewScale={viewScale} muted onDelete={() => onDelete(measurement.id)} testid={`measurement-label-${measurement.id}`} />
      )}
    </g>
  );
}


function LabelChip({ x, y, text, viewScale, muted, onDelete, testid }) {
  // LAST-CHANCE RESCUE — saved measurement label uses halo text, not a
  // black chip. Tap fires the delete confirm via the parent. Hover shows
  // a subtle red tint so the operator knows the tap is destructive.
  const [hover, setHover] = useState(false);
  const fg = hover ? "#DC2626" : (muted ? "#C2410C" : "var(--fop-primary)");
  const display = hover ? "✕ delete" : text;
  // Wider hit region for touch — invisible rect under the text.
  const hitW = Math.max(72, text.length * 9);
  return (
    <g
      style={{ pointerEvents: "auto", cursor: "pointer" }}
      onPointerDown={(e) => e.stopPropagation()}
      onClick={(e) => { e.stopPropagation(); onDelete && onDelete(); }}
      onPointerEnter={() => setHover(true)}
      onPointerLeave={() => setHover(false)}
      transform={`translate(${x}, ${y})`}
      data-testid={testid}
    >
      <rect
        x={-hitW / 2 / viewScale} y={-12 / viewScale}
        width={hitW / viewScale} height={24 / viewScale}
        fill="transparent"
      />
      <text
        x={0} y={0}
        textAnchor="middle" dominantBaseline="middle"
        fontFamily="ui-monospace,Menlo,monospace"
        fontSize={11 / viewScale}
        fontWeight="700"
        fill={fg}
        stroke="#ffffff"
        strokeWidth={3.5 / viewScale}
        strokeLinejoin="round"
        paintOrder="stroke"
        style={{ paintOrder: "stroke" }}
      >
        {display}
      </text>
    </g>
  );
}
